import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CoreService } from './core.service';

export interface HrIntegrationState {
  cmpHrIsIntegrated: number; 
  progress: number;
  currentStep: string;
  hrUrl: string | null;
  isPolling: boolean;
  failureReason: string | null;
}

const STORAGE_KEY = 'hr_integration_polling_active';

@Injectable({
  providedIn: 'root'
})
export class HrIntegrationService implements OnDestroy {

  private readonly POLL_INTERVAL_MS = 10 * 1000;
  private pollSubscription: Subscription | null = null;
  private branchId: any = null;

  private stateSubject = new BehaviorSubject<HrIntegrationState>({
    cmpHrIsIntegrated: 0,
    progress: 0,
    currentStep: '',
    hrUrl: null,
    isPolling: false,
    failureReason: null
  });

  state$ = this.stateSubject.asObservable();

  constructor(private apiService: ApiService, private coreService: CoreService) { }

  init(): void {
    this.branchId = this.coreService.getUserData('branch_id');

    this.apiService.getHrIntegrationStatus().subscribe(
      (res: any) => {
        if (!res.status) return;

        this.updateState({
          cmpHrIsIntegrated: res.cmp_hr_is_integrated,
          hrUrl: res.hr_url
        });

        if (res.cmp_hr_is_integrated === 2) {
          this.startPolling(this.branchId);
        }
      },
      (error) => console.log(error)
    );
  }

  startPolling(branchId?: any): void {
    if (branchId !== undefined && branchId !== null) {
      this.branchId = branchId;
    }

    if (this.pollSubscription) {
      return; 
    }

    localStorage.setItem(STORAGE_KEY, '1');
    this.updateState({ isPolling: true });

    this.pollSubscription = timer(0, this.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.apiService.checkSiteCreationStatus(this.branchId)),
        takeWhile((res: any) => this.shouldContinuePolling(res), true) 
      )
      .subscribe(
        (res: any) => this.handleStatusResponse(res),
        (error) => {
          console.log('Polling error:', error);
        }
      );
  }

  stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
    localStorage.removeItem(STORAGE_KEY);
    this.updateState({ isPolling: false });
  }

  private shouldContinuePolling(res: any): boolean {
    if (!res.status) return true; 
    return res.cmp_hr_is_integrated === 2 && res.erp_status !== 'failed';
  }

  private handleStatusResponse(res: any): void {
    if (!res.status) {
      console.log(res.message);
      return;
    }

    this.updateState({
      cmpHrIsIntegrated: res.cmp_hr_is_integrated,
      progress: res.progress || 0,
      currentStep: res.current_step || ''
    });

    if (res.cmp_hr_is_integrated === 1) {
      this.stopPolling();
      this.updateState({ hrUrl: res.hr_url });

    } else if (res.erp_status === 'failed') {
      this.stopPolling();
      this.updateState({
        cmpHrIsIntegrated: 0,
        failureReason: res.failure_reason || 'Site creation failed'
      });
    }
  }

  private updateState(partial: Partial<HrIntegrationState>): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partial
    });
  }

  getCurrentState(): HrIntegrationState {
    return this.stateSubject.value;
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}